import React, { useReducer, useState } from "react";
import { getFixtureById } from "../../services/MatchService";
import MatchH2H from "./components/MatchH2H";
import MatchStandings from "./components/MatchStandings";
import MatchHero from "./components/MatchHero";
import Layout from "../../components/layout/Layout";
import { FaBattleNet, FaChartArea, FaChessBoard, FaFutbol, FaList, FaProjectDiagram } from "react-icons/fa";
import MatchStats from "./components/MatchStats";
import MatchReview from "./components/MatchReview";
import MatchLineups from "./components/MatchLineups";
import { initialState, reducer } from "./reducer";
import MatchAnalytics from "./components/MatchAnalytics";
import { useSelector } from "react-redux";
import CompetitionServiceInstance from "../../services/CompetitionService";


export const CDN_TEAM_LOGO = (id) =>
    id ? `https://media.api-sports.io/football/teams/${id}.png` : undefined;

export const CDN_LEAGUE_LOGO = (id) =>
    id ? `https://media.api-sports.io/football/leagues/${id}.png` : undefined;

export const isFinished = (s) => ["FT", "AET", "PEN"].includes(s ?? "");

const readFixtureId = () => {
    const m = window.location.pathname.match(/^\/match\/(\d+)(?:\/|$)/);
    return m ? Number(m[1]) : null;
};


function extractTables(data) {
    const root = data?.response?.[0]?.league?.standings;
    if (!Array.isArray(root)) return [];
    const groups = Array.isArray(root[0]) ? root : [root];
    return groups.map((grpArr, i) => {
        const first = grpArr?.[0];
        const title = first?.group || (groups.length > 1 ? `Group ${i + 1}` : "");
        const rows = (grpArr || []).map((r) => ({
            description: r?.description,
            group: r?.group,
            rank: r?.rank,
            teamId: r?.team?.id,
            teamName: r?.team?.name,
            played: r?.all?.played,
            win: r?.all?.win,
            draw: r?.all?.draw,
            lose: r?.all?.lose,
            gf: r?.all?.goals?.for,
            ga: r?.all?.goals?.against,
            pts: r?.points,
            form: r?.form || "",
        }));
        return { title, rows };
    });
}

const TABS = [
    { key: "match", label: "Match", Icon: FaFutbol },
    { key: "statistics", label: "Statistics", Icon: FaChartArea },
    { key: "lineup", label: "Lineup", Icon: FaChessBoard },
    { key: "h2h", label: "H2H", Icon: FaBattleNet },
    { key: "standings", label: "Standings", Icon: FaList },
    { key: "analytics", label: "Analytics", Icon: FaProjectDiagram },
];

export default function MatchDetailPage({ rapidKey = process.env.REACT_APP_RAPIDAPI_KEY, initialTab = "match" }) {
    const fixtureId = readFixtureId();
    const token = useSelector((state) => state.Auth.token);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [matchData, setMatchData] = useState(null);
    const [tab, setTab] = React.useState(initialTab);
    const fetchingFixtureRef = React.useRef(false);
    const lastStandingsKeyRef = React.useRef("");
    const lastTeamsKeyRef = React.useRef("");

    const isActive = (key) => tab === key;

    React.useEffect(() => {
        if (!token || !fixtureId) return;

        let cancelled = false;

        (async () => {
            try {
                const response = await CompetitionServiceInstance.getFixtureDetails(token, fixtureId);
                const responseData = response.data;
                const { success, result } = responseData;
                if (success === true) {
                    setMatchData(result);
                }
            } catch (err) {

            } finally {
                if (!cancelled) setMatchData(null);
            }
        })();

        return () => { cancelled = true; };
    }, [token, fixtureId]);

    React.useEffect(() => {
        if (!rapidKey || !fixtureId) return;

        let cancelled = false;
        fetchingFixtureRef.current = true;

        (async () => {
            try {
                dispatch({ type: "set_loading", payload: true });
                const data = await getFixtureById({ key: rapidKey, fixtureId });
                if (cancelled) return;
                dispatch({ type: "set_fixture", payload: data });
            } catch (err) {
            } finally {
                fetchingFixtureRef.current = false;
                if (!cancelled) dispatch({ type: "set_loading", payload: false });
            }
        })();

        return () => { cancelled = true; };
    }, [rapidKey, fixtureId, state?.fixture?.fixture?.id]);

    React.useEffect(() => {
        const leagueId = state.leagueId;
        const season = state.season;
        if (!leagueId || !season || !rapidKey) return;

        const key = `${leagueId}:${season}`;
        if (lastStandingsKeyRef.current === key) return;

        const ctrl = new AbortController();
        (async () => {
            try {
                const res = await fetch(
                    `https://v3.football.api-sports.io/standings?league=${leagueId}&season=${season}`,
                    {
                        headers: {
                            "x-apisports-key": rapidKey,
                        },
                        signal: ctrl.signal,
                    }
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                dispatch({ type: "set_standings", payload: extractTables(data) });
                lastStandingsKeyRef.current = key;
            } catch { }
        })();
        return () => ctrl.abort();
    }, [state.leagueId, state.season, rapidKey]);

    React.useEffect(() => {
        const hId = state?.homeTeam?.id;
        const aId = state?.awayTeam?.id;
        if (!hId || !aId || !rapidKey) return;

        const key = `${hId}:${aId}`;

        const ctrl = new AbortController();
        (async () => {
            try {
                // H2H
                const r1 = await fetch(
                    `https://v3.football.api-sports.io/fixtures/headtohead?h2h=${hId}-${aId}&last=50`,
                    {
                        headers: {
                            "x-apisports-key": rapidKey,
                        },
                        signal: ctrl.signal,
                    }
                );
                if (!r1.ok) throw new Error(`H2H HTTP ${r1.status}`);
                const d1 = await r1.json();
                dispatch({ type: "set_h2h", payload: d1?.response || [] });

                // last 5 (both)
                const [r2, r3] = await Promise.all([
                    fetch(`https://v3.football.api-sports.io/fixtures?team=${hId}&last=${state.lastMatches}`, {
                        headers: {
                            "x-apisports-key": rapidKey,
                        },
                        signal: ctrl.signal,
                    }),
                    fetch(`https://v3.football.api-sports.io/fixtures?team=${aId}&last=${state.lastMatches}`, {
                        headers: {
                            "x-apisports-key": rapidKey,
                        },
                        signal: ctrl.signal,
                    }),
                ]);
                if (!r2.ok || !r3.ok) throw new Error("Team last fixtures error");

                const [d2, d3] = await Promise.all([r2.json(), r3.json()]);
                const homeFixtures = d2?.response || [];
                const awayFixtures = d3?.response || [];
                dispatch({ type: "set_home_fixtures", payload: homeFixtures });
                dispatch({ type: "set_away_fixtures", payload: awayFixtures });

                const winnerSide = (f) =>
                    f?.teams?.home?.winner ? "home" : f?.teams?.away?.winner ? "away" : "draw";
                const teamForm = (arr, teamId) =>
                    (arr || [])
                        .map((f) => {
                            const w = winnerSide(f);
                            if (w === "draw") return "D";
                            const isHome = f?.teams?.home?.id === teamId;
                            return (w === "home" && isHome) || (w === "away" && !isHome) ? "W" : "L";
                        })
                        .join("")
                        .slice(0, 5);

                dispatch({ type: "set_home_form", payload: teamForm(homeFixtures, hId) });
                dispatch({ type: "set_away_form", payload: teamForm(awayFixtures, aId) });

                lastTeamsKeyRef.current = key;
            } catch { }
        })();

        return () => ctrl.abort();
    }, [state?.homeTeam?.id, state?.awayTeam?.id, state?.lastMatches, rapidKey]);

    const onKickoff = () => { return };

    const linkRefs = React.useRef({});
    React.useEffect(() => {
        const el = linkRefs.current[tab];
        if (el && el.scrollIntoView) {
            el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        }
    }, [tab]);

    return (
        <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="title">
            <div className='w-100' style={{ backgroundColor: '#f5f5f5' }}>
                {state?.fixture && <MatchHero fixture={state?.fixture} matchData={matchData} onKickoff={onKickoff} />}
                <nav
                    className="px-subnav"
                    aria-label="Match sections"
                >
                    <div className="px-subnav__inner" role="tablist" aria-orientation="horizontal">
                        {TABS.map(({ key, label, Icon }) => (
                            <button
                                key={key}
                                role="tab"
                                ref={(el) => (linkRefs.current[key] = el)}
                                aria-selected={isActive(key)}
                                tabIndex={isActive(key) ? 0 : -1}
                                onClick={() => setTab(key)}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                                        e.preventDefault();
                                        const idx = TABS.findIndex(t => t.key === tab);
                                        const next = e.key === 'ArrowRight'
                                            ? (idx + 1) % TABS.length
                                            : (idx - 1 + TABS.length) % TABS.length;
                                        setTab(TABS[next].key);
                                    }
                                }}
                                className={`px-subnav__link ${isActive(key) ? 'is-active' : ''}`}
                            >
                                <Icon aria-hidden focusable="false" />
                                <span>{label}</span>
                            </button>
                        ))}
                    </div>
                </nav>

                {tab === 'standings' && (
                    <MatchStandings
                        fixture={state?.fixture}
                        homeId={state?.homeTeam?.id}
                        awayId={state?.awayTeam?.id}
                        standings={state?.standings}
                    />
                )}

                {tab === 'h2h' && (
                    <MatchH2H
                        t1={state?.homeTeam?.id}
                        t2={state?.awayTeam?.id}
                        t1Name={state?.fixture?.teams?.home?.name}
                        t2Name={state?.fixture?.teams?.away?.name}
                        t1Logo={state?.fixture?.teams?.home?.logo}
                        t2logo={state?.fixture?.teams?.away?.logo}
                        form1={state?.homeForm}
                        form2={state?.awayForm}
                        last1={state?.homeFixtures}
                        last2={state?.awayFixtures}
                        h2h={state?.h2h}
                    />
                )}

                {tab === 'analytics' && (
                    <MatchAnalytics
                        dispatch={dispatch}
                        t1={state?.homeTeam?.id}
                        t2={state?.awayTeam?.id}
                        t1Name={state?.fixture?.teams?.home?.name}
                        t2Name={state?.fixture?.teams?.away?.name}
                        t1Logo={state?.fixture?.teams?.home?.logo}
                        t2logo={state?.fixture?.teams?.away?.logo}
                        form1={state?.homeForm}
                        form2={state?.awayForm}
                        last1={state?.homeFixtures}
                        last2={state?.awayFixtures}
                        lastMatches={state?.lastMatches}
                        h2h={state?.h2h}
                    />
                )}

                {tab === 'statistics' && state?.fixture && (
                    <MatchStats
                        fixture={state.fixture}
                        fixtureId={state.fixture?.fixture?.id}
                        home={{ id: state.fixture?.teams?.home?.id, name: state.fixture?.teams?.home?.name, color: "#2563eb" }}
                        away={{ id: state.fixture?.teams?.away?.id, name: state.fixture?.teams?.away?.name, color: "#16a34a" }}
                        pairs={state.statisticPairs || null}
                        statistics={state.statistics || []}
                    />
                )}

                {tab === 'match' && state?.fixture && (
                    <MatchReview
                        fixture={state.fixture}
                        homeId={state.fixture?.teams?.home?.id}
                        awayId={state.fixture?.teams?.away?.id}
                        events={state.events || []}
                    />
                )}

                {tab === 'lineup' && state?.fixture && (
                    <MatchLineups
                        fixtureId={state.fixture?.fixture?.id}
                        fixture={state.fixture}
                        lineups={state.lineups || []}
                    />
                )}

            </div>
            <style>{`
  /* Sticky subnav */
  .px-subnav {
    top: var(--subnav-top, 0); /* sits under your header */
    z-index: 40;
    backdrop-filter: blur(6px);
    background: rgba(255,255,255,.85);
    border-bottom: 1px solid rgba(2,6,23,.08);
  }

  .px-subnav__inner {
    max-width: 1450px;
    margin: 0 auto;
    padding: 10px 16px;
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    position: relative;
  }
  .px-subnav__inner::-webkit-scrollbar { display: none; }

  /* Edge fade hints */
  .px-subnav__inner::before,
  .px-subnav__inner::after {
    content: "";
    position: sticky;
    top: 0;
    width: 24px;
    height: 36px;
    flex: 0 0 auto;
    pointer-events: none;
    z-index: 1;
  }
  .px-subnav__inner::before {
    left: 0;
    margin-left: -16px;
    background: linear-gradient(90deg, rgba(255,255,255,.85), rgba(255,255,255,0));
  }
  .px-subnav__inner::after {
    margin-right: -16px;
    right: 0;
    background: linear-gradient(270deg, rgba(255,255,255,.85), rgba(255,255,255,0));
  }

  /* Pills */
  .px-subnav__link {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    border: 1px solid #cbcbcb;
    background: #f5f5f5;
    white-space: nowrap;
    text-decoration: none;
  }
  .px-subnav__link:hover {
    background: #f1f5f9;
    border-color: rgba(2,6,23,.08);
  }
  .px-subnav__link.is-active,
  .px-subnav__link[aria-selected="true"] {
    color: #1e3a8a;
    background: rgba(37,99,235,.10);
    border-color: rgba(37,99,235,.25);
  }

  /* Focus-visible for keyboard users */
  .px-subnav__link:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  /* Compact on very small screens */
  @media (max-width: 400px) {
    .px-subnav__link { padding: 6px 10px; font-size: 12px; }
  }
`}</style>
        </Layout>
    );
}
